import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { Field, TextInput, TextArea, SelectInput } from "@/components/brand/Field";
import { Container, Section } from "@/components/layout/Section";
import { api, formatApiError } from "@/lib/api";
import { SITE } from "@/config/site";
import { ROUTE_SEO } from "@/config/seoData";

const BUDGET_RANGES = [
  "Under A$5,000",
  "A$5,000 – A$15,000",
  "A$15,000 – A$50,000",
  "A$50,000+",
  "Not sure yet",
];

const TIMELINES = ["As soon as possible", "Within 1–3 months", "3–6 months", "6+ months", "Flexible / exploring"];

const COUNTRIES = [
  "Australia", "India", "United States", "United Kingdom", "Canada", "New Zealand", "Singapore",
  "United Arab Emirates", "Germany", "Netherlands", "Ireland", "Other",
];

const newIdempotencyKey = () =>
  crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const Contact = () => {
  const [params] = useSearchParams();
  const preselectedService = params.get("service") || "";
  const sourcePage = params.get("source") || (preselectedService ? `/services/.../${preselectedService}` : "/contact");

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState({});
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    country: "Australia",
    service_slug: preselectedService,
    budget: "",
    timeline: "",
    message: "",
    consent: false,
    website: "", // honeypot — must stay empty
  });
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [state, setState] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");
  const [enquiryId, setEnquiryId] = useState("");

  useEffect(() => {
    api.get("/services?limit=200").then((r) => setServices(r.data)).catch(() => {});
    api
      .get("/services/categories")
      .then((r) => setCategories(Object.fromEntries(r.data.map((c) => [c.slug, c.name]))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (preselectedService) setForm((f) => ({ ...f, service_slug: preselectedService }));
  }, [preselectedService]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const groupedServices = useMemo(() => {
    const groups = {};
    for (const s of services) {
      (groups[s.category] = groups[s.category] || []).push(s);
    }
    return groups;
  }, [services]);

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setError("");
    try {
      const payload = { ...form, idempotency_key: idempotencyKey, source_page: sourcePage };
      const r = await api.post("/enquiries", payload);
      setEnquiryId(r.data.enquiry_id);
      setState("success");
    } catch (err) {
      setState("error");
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const reset = () => {
    setForm({
      full_name: "", email: "", phone: "", company: "", country: "Australia",
      service_slug: "", budget: "", timeline: "", message: "", consent: false, website: "",
    });
    setIdempotencyKey(newIdempotencyKey());
    setState("idle");
    setError("");
    setEnquiryId("");
  };

  return (
    <>
      <Seo {...ROUTE_SEO.contact} />
      <Section testid="contact-page" className="pt-32 sm:pt-40">
        <Container>
          <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
            {/* Left rail */}
            <div className="space-y-8 lg:col-span-5">
              <div className="space-y-5">
                <p className="eyebrow">Contact</p>
                <h1 className="font-display text-3xl font-bold tracking-tight text-brand-snow sm:text-4xl lg:text-5xl">
                  Tell us what you're building
                </h1>
                <p className="text-base leading-relaxed text-brand-mist sm:text-lg">
                  A senior engineer or strategist reviews every enquiry personally. Expect a straight answer on
                  approach, scope and fit — not a sales script.
                </p>
              </div>
              <ul className="space-y-5 text-sm text-brand-mist" data-testid="contact-details">
                <li>
                  <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="flex items-center gap-3 transition-colors hover:text-brand-emerald">
                    <Mail size={16} className="shrink-0 text-brand-emerald" /> {SITE.email}
                  </a>
                </li>
                <li>
                  <a href={SITE.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-3 transition-colors hover:text-brand-emerald">
                    <Phone size={16} className="shrink-0 text-brand-emerald" /> {SITE.phone}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="mt-0.5 shrink-0 text-brand-emerald" />
                  <span>{SITE.indiaOffice}</span>
                </li>
              </ul>
              <div className="flex items-start gap-3 rounded-xl border border-brand-mist/12 bg-brand-charcoal p-5 text-sm text-brand-mist" data-testid="contact-privacy-note">
                <ShieldCheck size={18} className="mt-0.5 shrink-0 text-brand-emerald" />
                <p>
                  We collect only what we need to respond to your enquiry. Nothing is shared, sold or added to a
                  mailing list. See our{" "}
                  <Link to="/privacy" className="text-brand-emerald underline-offset-4 hover:underline" data-testid="contact-privacy-link">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-7">
              {state === "success" ? (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6 rounded-2xl border border-brand-emerald/25 bg-brand-charcoal p-10"
                  data-testid="contact-success-panel"
                >
                  <CheckCircle2 size={40} className="text-brand-emerald" />
                  <div className="space-y-3">
                    <h2 className="font-display text-2xl font-bold text-brand-snow">Enquiry received</h2>
                    <p className="text-sm leading-relaxed text-brand-mist sm:text-base">
                      Thank you — your enquiry reference is{" "}
                      <span className="font-semibold text-brand-emerald" data-testid="contact-enquiry-id">{enquiryId}</span>.
                      A confirmation email is on its way to you, and our team will review the details and respond.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <Link
                      to="/services"
                      data-testid="contact-success-browse"
                      className="inline-flex items-center justify-center rounded-full border border-brand-mist/30 px-6 py-3 font-display text-sm font-semibold text-brand-snow transition-colors hover:border-brand-emerald hover:text-brand-emerald"
                    >
                      Browse more services
                    </Link>
                    <button
                      type="button"
                      onClick={reset}
                      data-testid="contact-success-new-enquiry"
                      className="text-sm font-medium text-brand-mist transition-colors hover:text-brand-snow"
                    >
                      Submit another enquiry
                    </button>
                  </div>
                </motion.div>
              ) : (
                <form onSubmit={submit} noValidate={false} className="surface-card space-y-6 p-6 sm:p-10" data-testid="contact-form">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Full name" htmlFor="full_name" required>
                      <TextInput id="full_name" testid="input-full-name" value={form.full_name} onChange={set("full_name")} autoComplete="name" required maxLength={120} placeholder="Jane Citizen" />
                    </Field>
                    <Field label="Business email" htmlFor="email" required>
                      <TextInput id="email" type="email" testid="input-email" value={form.email} onChange={set("email")} autoComplete="email" required placeholder="jane@company.com" />
                    </Field>
                    <Field label="Phone (with country code)" htmlFor="phone" hint="Optional — e.g. +61 4XX XXX XXX">
                      <TextInput id="phone" type="tel" testid="input-phone" value={form.phone} onChange={set("phone")} autoComplete="tel" maxLength={40} placeholder="+61 …" />
                    </Field>
                    <Field label="Company name" htmlFor="company">
                      <TextInput id="company" testid="input-company" value={form.company} onChange={set("company")} autoComplete="organization" maxLength={160} placeholder="Optional" />
                    </Field>
                    <Field label="Country" htmlFor="country" required>
                      <SelectInput id="country" testid="select-country" value={form.country} onChange={set("country")} required>
                        {COUNTRIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </SelectInput>
                    </Field>
                    <Field label="Service you're interested in" htmlFor="service_slug" required>
                      <SelectInput id="service_slug" testid="select-service" value={form.service_slug} onChange={set("service_slug")} required>
                        <option value="" disabled>
                          Select a service
                        </option>
                        {Object.entries(groupedServices).map(([cat, list]) => (
                          <optgroup key={cat} label={categories[cat] || cat}>
                            {list.map((s) => (
                              <option key={s.slug} value={s.slug}>{s.name}</option>
                            ))}
                          </optgroup>
                        ))}
                      </SelectInput>
                    </Field>
                    <Field label="Project budget" htmlFor="budget">
                      <SelectInput id="budget" testid="select-budget" value={form.budget} onChange={set("budget")}>
                        <option value="">Optional</option>
                        {BUDGET_RANGES.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </SelectInput>
                    </Field>
                    <Field label="Expected timeline" htmlFor="timeline">
                      <SelectInput id="timeline" testid="select-timeline" value={form.timeline} onChange={set("timeline")}>
                        <option value="">Optional</option>
                        {TIMELINES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </SelectInput>
                    </Field>
                  </div>
                  <Field label="Project details" htmlFor="message" required hint="What are you building or growing, and what does success look like?">
                    <TextArea id="message" testid="textarea-message" value={form.message} onChange={set("message")} required minLength={20} maxLength={5000} rows={6} placeholder="Tell us about your project, goals and constraints…" />
                  </Field>

                  {/* Honeypot: invisible to humans, bots fill it */}
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
                    <label htmlFor="website">Website</label>
                    <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="consent" className="flex items-start gap-3 text-sm text-brand-mist" data-testid="consent-label">
                      <input
                        id="consent"
                        type="checkbox"
                        checked={form.consent}
                        onChange={set("consent")}
                        required
                        data-testid="checkbox-consent"
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-brand-mist/40 bg-brand-charcoal accent-brand-emerald"
                      />
                      <span>
                        I consent to A2Z Rankings storing and processing these details to respond to my enquiry, as
                        described in the{" "}
                        <Link to="/privacy" className="text-brand-emerald underline-offset-4 hover:underline" data-testid="consent-privacy-link">
                          Privacy Policy
                        </Link>
                        .
                      </span>
                    </label>
                  </div>

                  {state === "error" && (
                    <p role="alert" data-testid="contact-form-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={state === "sending"}
                    data-testid="contact-submit-button"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-emerald px-8 py-4 font-display text-sm font-semibold text-brand-black transition-all duration-200 ease-brand hover:bg-brand-snow hover:shadow-glow-emerald disabled:opacity-60 sm:w-auto"
                  >
                    {state === "sending" ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      "Send enquiry"
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
};

export default Contact;
